import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Pagination,
  Paper,
  Select,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { toast } from "react-toastify";
import Header from "../../components/Header";
import ConfirmDialog from "../../components/Geners/ConfirmationDialog";
import api_service from "../../network/api_callbacks";

const QUESTION_SEARCH_LIMIT = 50;
const RAPSHORTS_QUESTION_GAME_TYPE = "rapShorts";
const DEFAULT_LIMIT = 10;
const DEFAULT_STATUS = "ACTIVE";
const DEFAULT_SORT_BY = "publishedAt";
const DEFAULT_SORT_ORDER = "desc";
const DEFAULT_CREATE_STATUS = "DRAFT";
const initialFilterValues = {
  status: DEFAULT_STATUS,
  sortBy: DEFAULT_SORT_BY,
  sortOrder: DEFAULT_SORT_ORDER,
  categoryId: "",
  tags: "",
  search: "",
  publishedAtFrom: "",
  publishedAtTo: "",
  expiresAtFrom: "",
  expiresAtTo: "",
  createdAtFrom: "",
  createdAtTo: "",
};

const normalizeResponse = (response) => {
  const list =
    response?.data?.data ||
    response?.data?.items ||
    response?.data?.rows ||
    response?.data?.docs ||
    response?.items ||
    response?.rows ||
    response?.docs ||
    response?.data ||
    [];

  const rows = Array.isArray(list) ? list : [];
  const pagination = response?.pagination || response?.data?.pagination || {};
  const pages = Number(pagination.pages || pagination.totalPages || 1);
  const total = Number(
    pagination.total ||
      pagination.totalItems ||
      response?.total ||
      response?.data?.total ||
      rows.length
  );

  return { rows, pages, total };
};

const formatDate = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleString();
};

const formatTags = (tags) => {
  if (!Array.isArray(tags) || tags.length === 0) return "-";
  return tags.join(", ");
};

const initialCreateForm = {
  title: "",
  body: "",
  bodyPreview: "",
  source: "",
  sourceUrl: "",
  imageUrl: "",
  questionId: "",
  publishedAt: "",
  expiresAt: "",
  categoryId: "",
  tags: "",
  status: DEFAULT_CREATE_STATUS,
};

const initialEditForm = {
  title: "",
  body: "",
  source: "",
  sourceUrl: "",
  imageUrl: "",
  questionId: "",
  publishedAt: "",
  tags: "",
  status: DEFAULT_CREATE_STATUS,
};

const toDateTimeLocalValue = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const timezoneOffset = date.getTimezoneOffset() * 60000;
  const localDate = new Date(date.getTime() - timezoneOffset);
  return localDate.toISOString().slice(0, 16);
};

const normalizeDetailResponse = (response) => {
  const inner = response?.data;
  if (inner && typeof inner === "object" && (inner._id || inner.id || inner.title)) {
    return inner;
  }
  return response?.data?.data || response?.data || response || {};
};

const getQuestionIdValue = (value) => {
  if (!value) return "";
  if (typeof value === "string") return value;
  if (typeof value === "object") return value?._id || value?.id || "";
  return String(value);
};

const formatQuestionOptions = (options) => {
  if (!Array.isArray(options) || options.length === 0) return "-";
  return options
    .map((opt) => `${opt.label || opt.id}: ${opt.text}`)
    .join("\n");
};

const formatQuestionAnswer = (answer) => {
  if (!Array.isArray(answer) || answer.length === 0) return "-";
  return answer.map((item) => item.id).filter(Boolean).join(", ");
};

const getQuestionOptionId = (option) =>
  option?.questionId || option?.id || option?._id || "";

const normalizeQuestionsListResponse = (response) => {
  const list = response?.data?.questions || [];
  return list.map((item) => {
    const questionId = getQuestionOptionId(item);
    return {
      ...item,
      questionId,
      id: questionId,
      questionText: item?.questionText || "",
    };
  });
};

const buildQuestionOptionFromDetail = (detail) => {
  const questionId = getQuestionIdValue(detail?.questionId);
  if (!questionId) return null;
  const nested = typeof detail?.questionId === "object" ? detail.questionId : null;
  return {
    questionId,
    id: questionId,
    questionText:
      nested?.questionText ||
      detail?.question?.questionText ||
      questionId,
    gameType: nested?.gameType,
    status: nested?.status,
  };
};

const mergeQuestionIntoOptions = (options, selected) => {
  if (!selected) return options;
  const selectedId = getQuestionOptionId(selected);
  if (!selectedId) return options;
  if (options.some((opt) => getQuestionOptionId(opt) === selectedId)) {
    return options;
  }
  return [selected, ...options];
};

const EditSection = ({ title, children }) => (
  <Box sx={{ mb: 2, width: "100%" }}>
    <Typography variant="subtitle1" sx={{ fontWeight: 600, color: "text.primary", mb: 1.5 }}>
      {title}
    </Typography>
    <Divider sx={{ mb: 2 }} />
    <Grid container spacing={2} sx={{ width: "100%" }}>
      {children}
    </Grid>
  </Box>
);

const ViewSection = ({ title, children }) => (
  <Box sx={{ mb: 2 }}>
    <Typography variant="subtitle1" sx={{ fontWeight: 600, color: "text.primary", mb: 1.5 }}>
      {title}
    </Typography>
    <Divider sx={{ mb: 2 }} />
    <Grid container spacing={2}>
      {children}
    </Grid>
  </Box>
);

const DetailField = ({ label, value, fullWidth = false }) => (
  <Grid item xs={12} sm={fullWidth ? 12 : 6}>
    <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
      {label}
    </Typography>
    <Typography variant="body2" sx={{ wordBreak: "break-word", whiteSpace: "pre-wrap" }}>
      {value ?? "-"}
    </Typography>
  </Grid>
);

const Rapshorts = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [createForm, setCreateForm] = useState(initialCreateForm);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [isLoadingEditDetail, setIsLoadingEditDetail] = useState(false);
  const [editingShortId, setEditingShortId] = useState("");
  const [editForm, setEditForm] = useState(initialEditForm);
  const [statusDraftById, setStatusDraftById] = useState({});
  const [updatingStatusById, setUpdatingStatusById] = useState({});
  const [deletingById, setDeletingById] = useState({});
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleteCandidate, setDeleteCandidate] = useState(null);
  const [isViewOpen, setIsViewOpen] = useState(false);
  const [isLoadingViewDetail, setIsLoadingViewDetail] = useState(false);
  const [viewDetail, setViewDetail] = useState(null);
  const [questionOptions, setQuestionOptions] = useState([]);
  const [questionOptionsLoading, setQuestionOptionsLoading] = useState(false);
  const [editSelectedQuestion, setEditSelectedQuestion] = useState(null);
  const questionSearchDebounceRef = useRef(null);
  const [page, setPage] = useState(1);
  const [limit] = useState(DEFAULT_LIMIT);
  const [filters, setFilters] = useState(initialFilterValues);
  const [appliedFilters, setAppliedFilters] = useState(initialFilterValues);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);

  const fetchRapshorts = useCallback(async () => {
    setLoading(true);
    try {
      const filterParams = Object.fromEntries(
        Object.entries(appliedFilters).filter(([, value]) => value !== "" && value !== null && value !== undefined)
      );
      const response = await api_service.getRapshortsList({
        page,
        limit,
        ...filterParams,
      });

      const { rows: apiRows, pages, total } = normalizeResponse(response);
      setRows(apiRows);
      setTotalPages(Math.max(pages, 1));
      setTotalRecords(total);
    } catch (error) {
      setRows([]);
      toast.error("Failed to fetch Rapshorts.");
    } finally {
      setLoading(false);
    }
  }, [page, limit, appliedFilters]);

  useEffect(() => {
    fetchRapshorts();
  }, [fetchRapshorts]);

  useEffect(() => {
    const nextDraftState = {};
    rows.forEach((item) => {
      const rowId = item?._id || item?.id;
      if (rowId) {
        nextDraftState[rowId] = item?.status || "DRAFT";
      }
    });
    setStatusDraftById(nextDraftState);
  }, [rows]);

  const getId = (item) => item?._id || item?.id || "-";
  const getTitle = (item) => item?.title || item?.name || item?.caption || "-";
  const getPublishedAt = (item) => item?.publishedAt || item?.publishDate || item?.createdAt;
  const getCreatedAt = (item) => item?.createdAt || item?.created_at;
  const handleFilterInputChange = (field, value) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
  };

  const applyFilters = () => {
    setPage(1);
    setAppliedFilters(filters);
  };

  const clearFilters = () => {
    setPage(1);
    setFilters(initialFilterValues);
    setAppliedFilters(initialFilterValues);
  };
  const hasAnyFilterChanges = Object.keys(initialFilterValues).some(
    (key) => filters[key] !== initialFilterValues[key]
  );

  const handleCreateInputChange = (field, value) => {
    setCreateForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleEditInputChange = (field, value) => {
    setEditForm((prev) => ({ ...prev, [field]: value }));
  };

  const resetCreateForm = () => {
    setCreateForm(initialCreateForm);
  };

  const handleCloseCreateDialog = () => {
    if (isCreating) return;
    setIsCreateOpen(false);
    resetCreateForm();
  };

  const handleCloseEditDialog = () => {
    if (isEditing) return;
    if (questionSearchDebounceRef.current) {
      clearTimeout(questionSearchDebounceRef.current);
      questionSearchDebounceRef.current = null;
    }
    setIsEditOpen(false);
    setEditingShortId("");
    setEditForm(initialEditForm);
    setIsLoadingEditDetail(false);
    setEditSelectedQuestion(null);
    setQuestionOptions([]);
  };

  const fetchQuestionOptions = useCallback(async (searchText = "", selectedQuestion = null) => {
    setQuestionOptionsLoading(true);
    try {
      const params = {
        page: 1,
        limit: QUESTION_SEARCH_LIMIT,
        gameType: [RAPSHORTS_QUESTION_GAME_TYPE],
      };
      if (searchText.trim()) {
        params.qText = searchText.trim();
      }

      const response = await api_service.getQuestionsNew(params);
      const responseCode = response?.responseCode;
      const status = response?.status;

      if (
        (responseCode && responseCode !== 200 && responseCode !== 201) ||
        (status && status !== 200 && status !== 201 && !responseCode)
      ) {
        throw new Error(response?.message || response?.error?.message || "Failed to fetch questions.");
      }

      const options = normalizeQuestionsListResponse(response);
      setQuestionOptions(mergeQuestionIntoOptions(options, selectedQuestion));
    } catch (error) {
      setQuestionOptions(selectedQuestion ? [selectedQuestion] : []);
      toast.error(error?.message || "Failed to fetch questions.");
    } finally {
      setQuestionOptionsLoading(false);
    }
  }, []);

  const scheduleQuestionSearch = useCallback(
    (searchText) => {
      if (questionSearchDebounceRef.current) {
        clearTimeout(questionSearchDebounceRef.current);
      }
      questionSearchDebounceRef.current = setTimeout(() => {
        fetchQuestionOptions(searchText, editSelectedQuestion);
      }, 400);
    },
    [fetchQuestionOptions, editSelectedQuestion]
  );

  const handleCloseViewDialog = () => {
    setIsViewOpen(false);
    setViewDetail(null);
    setIsLoadingViewDetail(false);
  };

  const openViewDialog = async (item) => {
    const shortId = getId(item);
    if (!shortId || shortId === "-") {
      toast.error("Invalid Rapshort ID.");
      return;
    }

    setIsViewOpen(true);
    setIsLoadingViewDetail(true);
    setViewDetail(null);
    try {
      const response = await api_service.getRapshortDetail({ shortId });
      if (response?.responseCode && response.responseCode !== 200) {
        throw new Error(response?.message || "Failed to load Rapshort details.");
      }

      const detail = normalizeDetailResponse(response);
      setViewDetail(detail);
    } catch (error) {
      toast.error(error?.message || "Failed to load Rapshort details.");
      setIsViewOpen(false);
    } finally {
      setIsLoadingViewDetail(false);
    }
  };

  const buildCreatePayload = () => {
    const publishedDate = new Date(createForm.publishedAt);
    const expiresDate = createForm.expiresAt ? new Date(createForm.expiresAt) : null;

    return {
      title: createForm.title.trim(),
      body: createForm.body.trim(),
      bodyPreview: createForm.bodyPreview.trim(),
      source: createForm.source.trim(),
      sourceUrl: createForm.sourceUrl.trim(),
      imageUrl: createForm.imageUrl.trim(),
      questionId: createForm.questionId.trim(),
      publishedAt: publishedDate.toISOString(),
      expiresAt: expiresDate ? expiresDate.toISOString() : null,
      categoryId: createForm.categoryId.trim() || null,
      tags: createForm.tags
        .split(",")
        .map((tag) => tag.trim())
        .filter(Boolean),
      status: createForm.status,
    };
  };

  const validateCreatePayload = () => {
    if (
      !createForm.title.trim() ||
      !createForm.body.trim() ||
      !createForm.bodyPreview.trim() ||
      !createForm.source.trim() ||
      !createForm.sourceUrl.trim() ||
      !createForm.imageUrl.trim() ||
      !createForm.questionId.trim() ||
      !createForm.publishedAt
    ) {
      toast.error("Please fill all required fields.");
      return false;
    }

    if (!/^[a-fA-F0-9]{24}$/.test(createForm.questionId.trim())) {
      toast.error("Question ID must be a 24-character hexadecimal ObjectId.");
      return false;
    }

    if (Number.isNaN(new Date(createForm.publishedAt).getTime())) {
      toast.error("Published At is invalid.");
      return false;
    }

    if (createForm.expiresAt && Number.isNaN(new Date(createForm.expiresAt).getTime())) {
      toast.error("Expires At is invalid.");
      return false;
    }

    return true;
  };

  const handleCreateRapshort = async () => {
    if (!validateCreatePayload()) return;

    setIsCreating(true);
    try {
      const payload = buildCreatePayload();
      const response = await api_service.createRapshort(payload);

      if (response?.responseCode && response.responseCode !== 200) {
        throw new Error(response?.message || "Failed to create Rapshort.");
      }

      if (response?.errors) {
        throw new Error(response?.message || "Failed to create Rapshort.");
      }

      toast.success("Rapshort created successfully.");
      setIsCreateOpen(false);
      resetCreateForm();
      setPage(1);
      await fetchRapshorts();
    } catch (error) {
      toast.error(error?.message || "Failed to create Rapshort.");
    } finally {
      setIsCreating(false);
    }
  };

  const openEditDialog = async (item) => {
    const shortId = getId(item);
    if (!shortId || shortId === "-") {
      toast.error("Invalid Rapshort ID.");
      return;
    }

    setEditingShortId(shortId);
    setIsEditOpen(true);
    setIsLoadingEditDetail(true);
    try {
      const response = await api_service.getRapshortDetail({ shortId });
      if (response?.responseCode && response.responseCode !== 200) {
        throw new Error(response?.message || "Failed to load Rapshort details.");
      }

      const detail = normalizeDetailResponse(response);
      const selectedQuestion = buildQuestionOptionFromDetail(detail);
      setEditForm({
        title: detail?.title || item?.title || "",
        body: detail?.body || item?.body || "",
        source: detail?.source || item?.source || "",
        sourceUrl: detail?.sourceUrl || item?.sourceUrl || "",
        imageUrl: detail?.imageUrl || item?.imageUrl || "",
        questionId: getQuestionIdValue(detail?.questionId || item?.questionId),
        publishedAt: toDateTimeLocalValue(detail?.publishedAt || item?.publishedAt),
        tags: Array.isArray(detail?.tags)
          ? detail.tags.join(", ")
          : Array.isArray(item?.tags)
          ? item.tags.join(", ")
          : "",
        status: detail?.status || item?.status || "DRAFT",
      });
      setEditSelectedQuestion(selectedQuestion);
      await fetchQuestionOptions("", selectedQuestion);
    } catch (error) {
      toast.error(error?.message || "Failed to load Rapshort details.");
      setIsEditOpen(false);
    } finally {
      setIsLoadingEditDetail(false);
    }
  };

  const validateEditPayload = () => {
    if (
      !editForm.title.trim() ||
      !editForm.body.trim() ||
      !editForm.source.trim() ||
      !editForm.sourceUrl.trim() ||
      !editForm.imageUrl.trim() ||
      !editForm.questionId.trim() ||
      !editForm.publishedAt
    ) {
      toast.error("Please fill all required fields.");
      return false;
    }

    if (!editSelectedQuestion || !/^[a-fA-F0-9]{24}$/.test(editForm.questionId.trim())) {
      toast.error("Please select a valid question.");
      return false;
    }

    if (Number.isNaN(new Date(editForm.publishedAt).getTime())) {
      toast.error("Published At is invalid.");
      return false;
    }

    return true;
  };

  const buildEditPayload = () => {
    const publishedDate = new Date(editForm.publishedAt);
    return {
      title: editForm.title.trim(),
      body: editForm.body.trim(),
      source: editForm.source.trim(),
      sourceUrl: editForm.sourceUrl.trim(),
      imageUrl: editForm.imageUrl.trim(),
      questionId: editForm.questionId.trim(),
      publishedAt: publishedDate.toISOString(),
      tags: editForm.tags
        .split(",")
        .map((tag) => tag.trim())
        .filter(Boolean),
      status: editForm.status,
    };
  };

  const handleEditRapshort = async () => {
    if (!editingShortId) {
      toast.error("Missing Rapshort ID.");
      return;
    }

    if (!validateEditPayload()) return;

    setIsEditing(true);
    try {
      const payload = buildEditPayload();
      const response = await api_service.editRapshort({
        shortId: editingShortId,
        data: payload,
      });

      if (response?.responseCode && response.responseCode !== 200) {
        throw new Error(response?.message || "Failed to edit Rapshort.");
      }

      if (response?.errors) {
        throw new Error(response?.message || "Failed to edit Rapshort.");
      }

      toast.success("Rapshort updated successfully.");
      handleCloseEditDialog();
      await fetchRapshorts();
    } catch (error) {
      toast.error(error?.message || "Failed to edit Rapshort.");
    } finally {
      setIsEditing(false);
    }
  };

  const handleUpdateStatus = async (item, nextStatus) => {
    const shortId = getId(item);
    if (!shortId || shortId === "-") {
      toast.error("Invalid Rapshort ID.");
      return;
    }

    setStatusDraftById((prev) => ({ ...prev, [shortId]: nextStatus }));
    setUpdatingStatusById((prev) => ({ ...prev, [shortId]: true }));
    try {
      const response = await api_service.updateRapshortStatus({
        shortId,
        status: nextStatus,
      });

      if (response?.responseCode && response.responseCode !== 200) {
        throw new Error(response?.message || "Failed to update status.");
      }

      if (response?.errors) {
        throw new Error(response?.message || "Failed to update status.");
      }

      toast.success("Status updated.");
      await fetchRapshorts();
    } catch (error) {
      toast.error(error?.message || "Failed to update status.");
    } finally {
      setUpdatingStatusById((prev) => ({ ...prev, [shortId]: false }));
    }
  };

  const handleDeleteRapshort = (item) => {
    const shortId = getId(item);
    if (!shortId || shortId === "-") {
      toast.error("Invalid Rapshort ID.");
      return;
    }

    setDeleteCandidate(item);
    setDeleteConfirmOpen(true);
  };

  const confirmDeleteRapshort = async () => {
    const shortId = getId(deleteCandidate);
    if (!shortId || shortId === "-") {
      toast.error("Invalid Rapshort ID.");
      setDeleteConfirmOpen(false);
      setDeleteCandidate(null);
      return;
    }

    setDeletingById((prev) => ({ ...prev, [shortId]: true }));
    try {
      const response = await api_service.deleteRapshort({ shortId });

      if (response?.responseCode && response.responseCode !== 200) {
        throw new Error(response?.message || "Failed to delete Rapshort.");
      }

      if (response?.errors) {
        throw new Error(response?.message || "Failed to delete Rapshort.");
      }

      toast.success("Rapshort deleted.");
      setDeleteConfirmOpen(false);
      setDeleteCandidate(null);
      await fetchRapshorts();
    } catch (error) {
      toast.error(error?.message || "Failed to delete Rapshort.");
    } finally {
      setDeletingById((prev) => ({ ...prev, [shortId]: false }));
    }
  };

  return (
    <Box id="rapshorts-page" sx={{ width: "100%", px: 3, pt: 3 }}>
      <Header title="" subtitle={`Rapshorts (${totalRecords})`} />

      <Box sx={{ mb: 2, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <Typography variant="h5" sx={{ fontWeight: "bold" }}>
          RAPSHORTS
        </Typography>
        <Button variant="contained" color="secondary" onClick={() => setIsCreateOpen(true)}>
          Create Rapshort
        </Button>
      </Box>

      <Paper elevation={3} sx={{ p: 2, mb: 2 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: "bold", mb: 2 }}>
          Filters
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              label="Search"
              fullWidth
              size="small"
              value={filters.search}
              onChange={(e) => handleFilterInputChange("search", e.target.value)}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <TextField
              label="Category ID"
              fullWidth
              size="small"
              value={filters.categoryId}
              onChange={(e) => handleFilterInputChange("categoryId", e.target.value)}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              label="Tags"
              placeholder="sports"
              fullWidth
              size="small"
              value={filters.tags}
              onChange={(e) => handleFilterInputChange("tags", e.target.value)}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <TextField
              label="Published At From"
              type="date"
              fullWidth
              size="small"
              value={filters.publishedAtFrom}
              onChange={(e) => handleFilterInputChange("publishedAtFrom", e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              label="Published At To"
              type="date"
              fullWidth
              size="small"
              value={filters.publishedAtTo}
              onChange={(e) => handleFilterInputChange("publishedAtTo", e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              label="Expires At From"
              type="date"
              fullWidth
              size="small"
              value={filters.expiresAtFrom}
              onChange={(e) => handleFilterInputChange("expiresAtFrom", e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              label="Expires At To"
              type="date"
              fullWidth
              size="small"
              value={filters.expiresAtTo}
              onChange={(e) => handleFilterInputChange("expiresAtTo", e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <TextField
              label="Created At From"
              type="date"
              fullWidth
              size="small"
              value={filters.createdAtFrom}
              onChange={(e) => handleFilterInputChange("createdAtFrom", e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              label="Created At To"
              type="date"
              fullWidth
              size="small"
              value={filters.createdAtTo}
              onChange={(e) => handleFilterInputChange("createdAtTo", e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>

          <Grid item xs={12} sm={4}>
            <FormControl fullWidth size="small">
              <InputLabel>Status</InputLabel>
              <Select
                label="Status"
                value={filters.status}
                onChange={(e) => handleFilterInputChange("status", e.target.value)}
              >
                <MenuItem value="ACTIVE">Active</MenuItem>
                <MenuItem value="DRAFT">Draft</MenuItem>
                <MenuItem value="EXPIRED">Expired</MenuItem>
                <MenuItem value="ALL">All</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={4}>
            <FormControl fullWidth size="small">
              <InputLabel>Sort By</InputLabel>
              <Select
                label="Sort By"
                value={filters.sortBy}
                onChange={(e) => handleFilterInputChange("sortBy", e.target.value)}
              >
                <MenuItem value="publishedAt">Published At</MenuItem>
                <MenuItem value="expiresAt">Expires At</MenuItem>
                <MenuItem value="created_at">Created At</MenuItem>
                <MenuItem value="updated_at">Updated At</MenuItem>
                <MenuItem value="title">Title</MenuItem>
                <MenuItem value="likes">Likes</MenuItem>
                <MenuItem value="shares">Shares</MenuItem>
                <MenuItem value="comments">Comments</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={4}>
            <FormControl fullWidth size="small">
              <InputLabel>Sort Order</InputLabel>
              <Select
                label="Sort Order"
                value={filters.sortOrder}
                onChange={(e) => handleFilterInputChange("sortOrder", e.target.value)}
              >
                <MenuItem value="desc">Descending</MenuItem>
                <MenuItem value="asc">Ascending</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12}>
            <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end" }}>
              <Button variant="contained" color="secondary" onClick={applyFilters}>
                Apply Filters
              </Button>
              <Button variant="outlined" color="black" onClick={clearFilters} disabled={!hasAnyFilterChanges}>
                Clear Filters
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      <TableContainer component={Paper} elevation={3}>
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell>ID</TableCell>
              <TableCell>Title</TableCell>
              <TableCell>Entity</TableCell>
              <TableCell>Source</TableCell>
              <TableCell>Tags</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Views</TableCell>
              <TableCell>Published At</TableCell>
              <TableCell>Created At</TableCell>
              <TableCell>Action</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={10} align="center">
                  <CircularProgress size={24} />
                </TableCell>
              </TableRow>
            ) : rows.length > 0 ? (
              rows.map((item) => (
                <TableRow key={getId(item)} hover>
                  <TableCell>{getId(item)}</TableCell>
                  <TableCell>
                    <Typography
                      component="span"
                      onClick={() => openViewDialog(item)}
                      sx={{
                        color: "secondary.main",
                        cursor: "pointer",
                        textDecoration: "underline",
                        "&:hover": { opacity: 0.85 },
                      }}
                    >
                      {getTitle(item)}
                    </Typography>
                  </TableCell>
                  <TableCell>{item?.entityName || "-"}</TableCell>
                  <TableCell>{item?.source || "-"}</TableCell>
                  <TableCell>{formatTags(item?.tags)}</TableCell>
                  <TableCell>{item?.status || "-"}</TableCell>
                  <TableCell>{item?.engagementStats?.views ?? 0}</TableCell>
                  <TableCell>{formatDate(getPublishedAt(item))}</TableCell>
                  <TableCell>{formatDate(getCreatedAt(item))}</TableCell>
                  <TableCell>
                    <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                      <FormControl size="small" sx={{ minWidth: 130 }}>
                        <Select
                          value={statusDraftById[getId(item)] || item?.status || "DRAFT"}
                          onChange={(e) => handleUpdateStatus(item, e.target.value)}
                          disabled={Boolean(updatingStatusById[getId(item)])}
                        >
                          <MenuItem value="DRAFT">Draft</MenuItem>
                          <MenuItem value="ACTIVE">Active</MenuItem>
                          <MenuItem value="EXPIRED">Expired</MenuItem>
                        </Select>
                      </FormControl>
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => openEditDialog(item)}
                        sx={{
                          color: "#C9A227",
                          borderColor: "#C9A227",
                          "&:hover": {
                            borderColor: "#B08D1F",
                            backgroundColor: "rgba(201, 162, 39, 0.08)",
                          },
                        }}
                      >
                        Edit
                      </Button>
                      <Button
                        variant="outlined"
                        color="error"
                        size="small"
                        onClick={() => handleDeleteRapshort(item)}
                        disabled={Boolean(deletingById[getId(item)])}
                      >
                        {deletingById[getId(item)] ? "Deleting..." : "Delete"}
                      </Button>
                    </Box>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={10} align="center">
                  No Rapshorts found.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Box display="flex" justifyContent="center" mt={2}>
        <Pagination
          count={totalPages}
          page={page}
          onChange={(_, nextPage) => setPage(nextPage)}
          color="secondary"
          showFirstButton
          showLastButton
        />
      </Box>

      <Dialog open={isCreateOpen} onClose={handleCloseCreateDialog} maxWidth="md" fullWidth>
        <DialogTitle>Create Rapshort</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12}>
              <TextField
                label="Title *"
                fullWidth
                value={createForm.title}
                onChange={(e) => handleCreateInputChange("title", e.target.value)}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Body *"
                fullWidth
                multiline
                minRows={3}
                value={createForm.body}
                onChange={(e) => handleCreateInputChange("body", e.target.value)}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Body Preview *"
                fullWidth
                multiline
                minRows={2}
                value={createForm.bodyPreview}
                onChange={(e) => handleCreateInputChange("bodyPreview", e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Source *"
                fullWidth
                value={createForm.source}
                onChange={(e) => handleCreateInputChange("source", e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Question ID *"
                fullWidth
                placeholder="24-char hex ObjectId"
                value={createForm.questionId}
                onChange={(e) => handleCreateInputChange("questionId", e.target.value)}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Source URL *"
                fullWidth
                value={createForm.sourceUrl}
                onChange={(e) => handleCreateInputChange("sourceUrl", e.target.value)}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Image URL *"
                fullWidth
                value={createForm.imageUrl}
                onChange={(e) => handleCreateInputChange("imageUrl", e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Published At *"
                type="datetime-local"
                fullWidth
                value={createForm.publishedAt}
                onChange={(e) => handleCreateInputChange("publishedAt", e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Expires At"
                type="datetime-local"
                fullWidth
                value={createForm.expiresAt}
                onChange={(e) => handleCreateInputChange("expiresAt", e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Category ID"
                fullWidth
                value={createForm.categoryId}
                onChange={(e) => handleCreateInputChange("categoryId", e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  label="Status"
                  value={createForm.status}
                  onChange={(e) => handleCreateInputChange("status", e.target.value)}
                >
                  <MenuItem value="DRAFT">Draft</MenuItem>
                  <MenuItem value="ACTIVE">Active</MenuItem>
                  <MenuItem value="EXPIRED">Expired</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Tags (comma separated)"
                fullWidth
                value={createForm.tags}
                onChange={(e) => handleCreateInputChange("tags", e.target.value)}
                placeholder="entertainment, sports"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleCloseCreateDialog} disabled={isCreating}>
            Cancel
          </Button>
          <Button variant="contained" color="secondary" onClick={handleCreateRapshort} disabled={isCreating}>
            {isCreating ? "Creating..." : "Create"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={isEditOpen}
        onClose={handleCloseEditDialog}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { maxHeight: "90vh" } }}
      >
        <DialogTitle sx={{ pb: 1 }}>Edit Rapshort</DialogTitle>
        <Divider />
        <DialogContent dividers sx={{ pt: 2, overflow: "auto" }}>
          {isLoadingEditDetail ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress size={28} />
            </Box>
          ) : (
            <Box sx={{ width: "100%" }}>
              <EditSection title="Content Details">
                <Grid item xs={12} sx={{ width: "100%" }}>
                  <TextField
                    label="Title *"
                    fullWidth
                    size="small"
                    sx={{ width: "100%" }}
                    value={editForm.title}
                    onChange={(e) => handleEditInputChange("title", e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} sx={{ width: "100%" }}>
                  <TextField
                    label="Body *"
                    fullWidth
                    size="small"
                    multiline
                    minRows={4}
                    sx={{ width: "100%" }}
                    value={editForm.body}
                    onChange={(e) => handleEditInputChange("body", e.target.value)}
                  />
                </Grid>
              </EditSection>

              <EditSection title="Question">
                <Grid item xs={12} sx={{ width: "100%" }}>
                  <Autocomplete
                    fullWidth
                    size="small"
                    sx={{ width: "100%" }}
                    options={questionOptions}
                    value={editSelectedQuestion}
                    loading={questionOptionsLoading}
                    onOpen={() => fetchQuestionOptions("", editSelectedQuestion)}
                    onInputChange={(_, value, reason) => {
                      if (reason === "input") {
                        scheduleQuestionSearch(value);
                      }
                    }}
                    onChange={(_, newValue) => {
                      setEditSelectedQuestion(newValue);
                      handleEditInputChange("questionId", getQuestionOptionId(newValue));
                    }}
                    getOptionLabel={(option) =>
                      typeof option === "string" ? option : option?.questionText || ""
                    }
                    isOptionEqualToValue={(option, value) =>
                      getQuestionOptionId(option) === getQuestionOptionId(value)
                    }
                    filterOptions={(options) => options}
                    noOptionsText={
                      questionOptionsLoading ? "Loading questions..." : "No questions found"
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        fullWidth
                        label="Question *"
                        placeholder="Search questions..."
                        sx={{ width: "100%" }}
                      />
                    )}
                  />
                </Grid>
              </EditSection>

              <EditSection title="Source & Media">
                <Grid item xs={12}>
                  <TextField
                    label="Source *"
                    fullWidth
                    size="small"
                    value={editForm.source}
                    onChange={(e) => handleEditInputChange("source", e.target.value)}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label="Source URL *"
                    fullWidth
                    size="small"
                    value={editForm.sourceUrl}
                    onChange={(e) => handleEditInputChange("sourceUrl", e.target.value)}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label="Image URL *"
                    fullWidth
                    size="small"
                    value={editForm.imageUrl}
                    onChange={(e) => handleEditInputChange("imageUrl", e.target.value)}
                  />
                </Grid>
              </EditSection>

              <EditSection title="Publish Controls">
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Published At *"
                    type="datetime-local"
                    fullWidth
                    size="small"
                    value={editForm.publishedAt}
                    onChange={(e) => handleEditInputChange("publishedAt", e.target.value)}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Status</InputLabel>
                    <Select
                      label="Status"
                      value={editForm.status}
                      onChange={(e) => handleEditInputChange("status", e.target.value)}
                    >
                      <MenuItem value="DRAFT">Draft</MenuItem>
                      <MenuItem value="ACTIVE">Active</MenuItem>
                      <MenuItem value="EXPIRED">Expired</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label="Tags (comma separated)"
                    fullWidth
                    size="small"
                    value={editForm.tags}
                    onChange={(e) => handleEditInputChange("tags", e.target.value)}
                    placeholder="entertainment, sports"
                  />
                </Grid>
              </EditSection>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
          <Button
            variant="outlined"
            color="inherit"
            onClick={handleCloseEditDialog}
            disabled={isEditing}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="secondary"
            onClick={handleEditRapshort}
            disabled={isEditing || isLoadingEditDetail}
          >
            {isEditing ? "Saving..." : "Save"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={isViewOpen}
        onClose={handleCloseViewDialog}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { maxHeight: "90vh" } }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          {viewDetail?.title || "Rapshort Details"}
        </DialogTitle>
        <Divider />
        <DialogContent dividers sx={{ pt: 2, overflow: "auto" }}>
          {isLoadingViewDetail ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress size={28} />
            </Box>
          ) : viewDetail ? (
            <Box>
              <ViewSection title="Overview">
                <DetailField label="ID" value={viewDetail?._id || viewDetail?.id} />
                <DetailField label="Status" value={viewDetail?.status} />
                <DetailField label="Entity" value={viewDetail?.entityName} />
                <DetailField label="Source" value={viewDetail?.source} />
                <DetailField label="Tags" value={formatTags(viewDetail?.tags)} fullWidth />
              </ViewSection>

              <ViewSection title="Content">
                <DetailField label="Title" value={viewDetail?.title} fullWidth />
                <DetailField label="Body" value={viewDetail?.body} fullWidth />
              </ViewSection>

              <ViewSection title="Question">
                <DetailField
                  label="Question ID"
                  value={getQuestionIdValue(viewDetail?.questionId)}
                />
                <DetailField label="Game Type" value={viewDetail?.questionId?.gameType} />
                <DetailField label="Question Status" value={viewDetail?.questionId?.status} />
                <DetailField
                  label="Question Text"
                  value={
                    viewDetail?.question?.questionText ||
                    viewDetail?.questionId?.questionText
                  }
                  fullWidth
                />
                <DetailField
                  label="Options"
                  value={formatQuestionOptions(
                    viewDetail?.question?.options || viewDetail?.questionId?.options
                  )}
                  fullWidth
                />
                <DetailField
                  label="Answer"
                  value={formatQuestionAnswer(
                    viewDetail?.question?.answer || viewDetail?.questionId?.answer
                  )}
                />
              </ViewSection>

              <ViewSection title="Publish">
                <DetailField label="Published At" value={formatDate(viewDetail?.publishedAt)} />
                <DetailField label="Created At" value={formatDate(viewDetail?.created_at)} />
                <DetailField label="Updated At" value={formatDate(viewDetail?.updated_at)} />
              </ViewSection>

              <ViewSection title="Engagement">
                <DetailField
                  label="Views"
                  value={viewDetail?.engagementStats?.views ?? 0}
                />
                <DetailField
                  label="Answers"
                  value={viewDetail?.engagementStats?.answers ?? 0}
                />
                <DetailField
                  label="Correct Answers"
                  value={viewDetail?.engagementStats?.correctAnswers ?? 0}
                />
              </ViewSection>

              {(viewDetail?.sourceUrl ||
                viewDetail?.imageUrl ||
                viewDetail?.categoryId ||
                viewDetail?.expiresAt) && (
                <ViewSection title="Additional">
                  {viewDetail?.categoryId ? (
                    <DetailField label="Category ID" value={viewDetail.categoryId} />
                  ) : null}
                  {viewDetail?.expiresAt ? (
                    <DetailField label="Expires At" value={formatDate(viewDetail.expiresAt)} />
                  ) : null}
                  {viewDetail?.sourceUrl ? (
                    <DetailField label="Source URL" value={viewDetail.sourceUrl} fullWidth />
                  ) : null}
                  {viewDetail?.imageUrl ? (
                    <>
                      <DetailField label="Image URL" value={viewDetail.imageUrl} fullWidth />
                      <Grid item xs={12}>
                        <Box
                          component="img"
                          src={viewDetail.imageUrl}
                          alt={viewDetail?.title || "Rapshort"}
                          sx={{
                            maxWidth: "100%",
                            maxHeight: 240,
                            borderRadius: 1,
                            border: "1px solid",
                            borderColor: "divider",
                          }}
                        />
                      </Grid>
                    </>
                  ) : null}
                </ViewSection>
              )}
            </Box>
          ) : null}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button variant="contained" color="secondary" onClick={handleCloseViewDialog}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        open={deleteConfirmOpen}
        title="Confirm Delete"
        content={`Are you sure you want to delete "${deleteCandidate?.title || "this Rapshort"}"?`}
        onClose={() => {
          if (!deleteCandidate || !deletingById[getId(deleteCandidate)]) {
            setDeleteConfirmOpen(false);
            setDeleteCandidate(null);
          }
        }}
        onConfirm={confirmDeleteRapshort}
      />
    </Box>
  );
};

export default Rapshorts;
